import * as yup from 'yup'
import { useApi } from '~/store/store'
import { LockIcon } from '~/assets/icons'
import { mutualTheme } from '~/utils/theme'
import { useParams } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, SubmitHandler } from 'react-hook-form'
import { AuthActions, trackEvent } from '~/service/analytics'
import React, { useCallback, useEffect, useState } from 'react'
import { validateNumericPassword } from '@mutual-libs/password-validation'
import { InputAdornment, FormControlLabel, Checkbox } from '@material-ui/core'
import { AuthTemplateView, Badge, HfPasswordInput, LoginSubmitButton, ResetLoading } from '~/components'

type ResetPasswordParams = {
  token: string,
  email: string
}

type FormValues = {
  password: string,
  passwordConfirmation: string
}

type ResetPasswordFormProps = {
  onSuccessfulResetPassword: () => void
}

const initialFormValues: FormValues = {
  password: '',
  passwordConfirmation: ''
}

const validationSchema = yup.object().shape({
  password: yup.string().required('A senha deve possuir 6 caracteres!')
    .length(6, 'A senha deve possuir 6 caracteres!')
    .matches(/^\d{6}$/, 'A senha deve conter apenas números!')
    .oneOf([yup.ref('passwordConfirmation'), null], 'As senhas devem coincidir!'),
  passwordConfirmation: yup.string().required()
})

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ onSuccessfulResetPassword }) => {
  const { token } = useParams<ResetPasswordParams>()

  const [visiblePassword, setVisiblePassword] = useState(false)
  const [validationError, setValidationError] = useState(false)

  const [resetAccountPassword, resetAccountPasswordState] = useApi('resetAccountPassword')
  const { loading, error } = resetAccountPasswordState

  const { handleSubmit, control, watch, formState: { isValid, isSubmitted, errors } } = useForm<FormValues>({
    defaultValues: initialFormValues,
    resolver: yupResolver(validationSchema)
  })

  const password = watch('password')

  const hasError = isSubmitted && (!isValid || validationError || error !== null)

  useEffect(() => {
    setValidationError(false)
  }, [password])

  const changePasswordVisibility = useCallback(() => {
    setVisiblePassword(!visiblePassword)
  }, [visiblePassword])

  const onSubmit: SubmitHandler<FormValues> = useCallback(async () => {
    const isValidPassword = validateNumericPassword(password, 6)

    if (!isValidPassword) {
      setValidationError(true)
      trackEvent(AuthActions.UNSUCCESSFUL_RESET_PASSWORD)
      return
    }

    const request = await resetAccountPassword({
      password,
      token
    })

    if (request.ok) {
      trackEvent(AuthActions.SUCCESSFUL_RESET_PASSWORD)
      onSuccessfulResetPassword()
    }
  }, [password])

  if (loading) {
    return <ResetLoading />
  }

  return (
    <AuthTemplateView
      title='Crie uma nova senha'
      description='Escolha uma nova senha para acessar sua conta.'
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <HfPasswordInput
          name='password'
          label='Digite a nova senha de 6 dígitos'
          sx={{
            marginTop: 5,
            marginBottom: 3
          }}
          disabled={loading}
          control={control}
          error={hasError}
          endAdornment={(
            <InputAdornment position='end'>
              <LockIcon htmlColor={mutualTheme.palette.textGray4.main} />
            </InputAdornment>
          )}
          isVisible={visiblePassword}
        />

        <HfPasswordInput
          name='passwordConfirmation'
          label='Repetir nova senha'
          disabled={loading}
          control={control}
          error={hasError}
          endAdornment={<InputAdornment position='end'><LockIcon htmlColor={mutualTheme.palette.textGray4.main} /></InputAdornment>}
          isVisible={visiblePassword}
        />

        <FormControlLabel
          label='Mostrar senha'
          control={(
            <Checkbox
              name='passwordVisibility'
              checked={visiblePassword}
              onChange={changePasswordVisibility}
            />
          )}
          sx={{
            color: 'textGray1.main',
            fontFamily: 'Airbnb',
            marginTop: 1
          }}
        />

        {hasError && (
          <Badge
            level='main'
            type='error'
            title={
              validationError && isValid
                ? 'Ops! A senha digitada não é segura'
                : errors.password?.message ?? 'Houve algum erro desconhecido'
            }
            description={
              validationError && isValid
                ? 'Não utilize números em sequência ou número repetidos. Exemplos: 123456, 123123, 232323, 112233'
                : 'Verifique as senhas informadas e tente novamente.'
            }
            icon='BlockIcon'
            hasError={hasError}
          />
        )}

        <LoginSubmitButton
          title='Definir nova senha'
          loading={loading}
        />
      </form>
    </AuthTemplateView>
  )
}
